import { RelayFragment } from 'src/shared/types';
import { ECommerce } from '../../products/__generated__/productList_models.graphql';
import { merchantProduct_item$data } from '../state/queries/__generated__/merchantProduct_item.graphql';
import { productFragment_model$data } from '../state/queries/__generated__/productFragment_model.graphql';
import { productProvider_listItem$data } from '../state/queries/__generated__/productProvider_listItem.graphql';
import { ColorAttribute } from '../steps/4-variants/helpers';
import { ProductProvider } from '../steps/pod-1-provider-selection/__generated__/providerSelectionQuery.graphql';
import { ModelArea } from './model/modelArea';
import { ModelColor } from './model/modelColor';
import { ModelColorMapping } from './model/modelColorMapping';
import { ModelOptions } from './model/modelOptions';
import { ModelSide } from './model/modelSide';
import SelectedOptionValue from './selectedOptionValue';
import { Side } from './side';
import { DisabledModelSide } from './state';

export const modeAction = (mode: 'none' | 'classic' | 'pod' | 'configurator') =>
    ({
        type: 'MODE',
        mode
    }) as const;

export const providerAction = (provider: ProductProvider) =>
    ({
        type: 'PROVIDER',
        provider
    }) as const;

export const setSalesChannelsAction = (
    salesChannelID: string,
    ecommerces: ECommerce[],
    salesChannels?: { id: string; ecommerceSettings?: { ecommerceName: ECommerce } | null }[]
) =>
    ({
        type: 'SET_SALES_CHANNELS',
        salesChannelID,
        ecommerces,
        salesChannels
    }) as const;

export const newProductAction = () =>
    ({
        type: 'NEW_PRODUCT'
    }) as const;

export const newProductNoIntegrationAction = () =>
    ({
        type: 'NEW_PRODUCT_NO_INTEGRATION'
    }) as const;

export const editProductNameAction = (productName: string) =>
    ({
        type: 'EDIT_PRODUCT_NAME',
        productName
    }) as const;

export const setProductImageAction = (imageUrl: string) =>
    ({
        type: 'SET_PRODUCT_IMAGE',
        imageUrl
    }) as const;

export const setProductProviderCodeAction = (providerCode: string | null) =>
    ({
        type: 'SET_PRODUCT_PROVIDER_CODE',
        providerCode
    }) as const;

export const selectMerchantProductAction = (
    merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>,
    customThumbnail: HTMLImageElement | null
) =>
    ({
        type: 'SELECT_MERCHANT_PRODUCT',
        merchantProduct,
        customThumbnail
    }) as const;

export const selectProviderProductAction = (providerProduct: Omit<RelayFragment<productProvider_listItem$data>, ' $fragmentType'>) =>
    ({
        type: 'SELECT_PROVIDER_PRODUCT',
        providerProduct
    }) as const;

export const addNewColorAction = (values: SelectedOptionValue[], mainSideImageUrl: string | null = null) =>
    ({
        type: 'ADD_NEW_COLOR',
        values,
        mainSideImageUrl
    }) as const;

export const removeColorAction = (color: ModelColor) =>
    ({
        type: 'REMOVE_COLOR',
        color
    }) as const;

export const removeAllColorsAction = () =>
    ({
        type: 'REMOVE_ALL_COLORS'
    }) as const;

export const editColorNameAction = (color: ModelColor, name: string) =>
    ({
        type: 'EDIT_COLOR_NAME',
        color,
        name
    }) as const;

export const editColorNameAndAttributesAction = (color: ModelColor, name: string, colorAttributes: ColorAttribute[]) =>
    ({
        type: 'EDIT_COLOR_NAME_AND_ATTRIBUTES',
        color,
        name,
        colorAttributes
    }) as const;

export const editColorMappingAction = (colorMapping: ModelColorMapping) =>
    ({
        type: 'EDIT_COLOR_MAPPING',
        colorMapping
    }) as const;

export const editColorJsonSettingsAction = (color: ModelColor, settings: { colorProviderCode: string | null; sideCodes: { [sideGuid: string]: string } }) =>
    ({
        type: 'EDIT_COLOR_JSON_SETTINGS',
        color,
        settings
    }) as const;

export const editModelColorMappingsSalesChannelsAction = (modelColorMappings: ModelColorMapping[]) =>
    ({
        type: 'EDIT_MODEL_COLOR_MAPPINGS_SALESCHANNELS',
        modelColorMappings
    }) as const;

export const swapColorsAction = (color1guid: string, color2guid: string) =>
    ({
        type: 'SWAP_COLORS',
        color1guid,
        color2guid
    }) as const;

// Reorder variants (colors) to match the given ordered list of color guids (used by drag-and-drop).
export const reorderColorsAction = (orderedColorGuids: string[]) =>
    ({
        type: 'REORDER_COLORS',
        orderedColorGuids
    }) as const;

export const addSideAction = (sideName: string) =>
    ({
        type: 'ADD_NEW_SIDE',
        sideName
    }) as const;

export const duplicateSideAction = (sourceSideGuid: string, sideName: string) =>
    ({
        type: 'DUPLICATE_SIDE',
        sourceSideGuid,
        sideName
    }) as const;

export const editSideNameAction = (modelSide: ModelSide, name: string) =>
    ({
        type: 'EDIT_SIDE',
        modelSide,
        name
    }) as const;

export const editSideMandatoryAllVariants = (modelSide: ModelSide, mandatory: boolean) =>
    ({
        type: 'EDIT_SIDE_MANDATORY_ALL_VARIANTS',
        modelSide,
        mandatory
    }) as const;

export const editSideMandatoryAllSides = (modelSide: ModelSide, mandatory: boolean) =>
    ({
        type: 'EDIT_SIDE_MANDATORY_ALL_SIDES',
        modelSide,
        mandatory
    }) as const;

export const editSideRotateAllVariants = (modelSide: ModelSide, canRotate: boolean) =>
    ({
        type: 'EDIT_SIDE_ROTATE_ALL_VARIANTS',
        modelSide,
        canRotate
    }) as const;

export const editSideRotateAllSides = (modelSide: ModelSide, canRotate: boolean) =>
    ({
        type: 'EDIT_SIDE_ROTATE_ALL_SIDES',
        modelSide,
        canRotate
    }) as const;

export const swapSidesAction = (side1guid: string, side2guid: string) =>
    ({
        type: 'SWAP_SIDES',
        side1guid,
        side2guid
    }) as const;

// Reorder sides to match the given ordered list of original side guids (used by drag-and-drop).
export const reorderSidesAction = (orderedSideGuids: string[]) =>
    ({
        type: 'REORDER_SIDES',
        orderedSideGuids
    }) as const;

export const mergeModelSideAction = (modelSide: ModelSide, newModelSide: Partial<ModelSide>) =>
    ({
        type: 'MERGE_MODEL_SIDE',
        modelSide,
        newModelSide
    }) as const;

// A side fully resolved by the AI product-creation flow: generated image + predicted print area + ppcm.
export interface AiResolvedSide {
    name: string;
    imageUrl: string;
    ppcm: number | null;
    area: ModelArea | null;
}

// Builds a brand-new product's sides from the AI-generated results in a single reducer pass: renames the
// default side (first), adds the remaining sides, applies each generated image + predicted area + ppcm across
// every color, and sets the product image from the first side.
export const applyAiSidesAction = (sides: AiResolvedSide[]) =>
    ({
        type: 'APPLY_AI_SIDES',
        sides
    }) as const;

export const addEmptyModelSideAction = (modelColor: ModelColor, side: Side) =>
    ({
        type: 'ADD_EMPTY_MODEL_SIDE',
        modelColor,
        side
    }) as const;

export const removeModelSideAction = (modelSide: ModelSide) =>
    ({
        type: 'REMOVE_MODEL_SIDE',
        modelSide
    }) as const;

export const removeSideAction = (side: Side) =>
    ({
        type: 'REMOVE_SIDE',
        side
    }) as const;

export const loadProductAction = (model: Omit<RelayFragment<productFragment_model$data>, ' $fragmentType'>) =>
    ({
        type: 'LOAD_PRODUCT',
        model
    }) as const;

export const changeStepAction = (step: string) =>
    ({
        type: 'CHANGE_STEP',
        step
    }) as const;

export const uploadSideImageStartAction = (modelSide: ModelSide) =>
    ({
        type: 'UPLOAD_SIDE_IMAGE_START',
        modelSide
    }) as const;

export const uploadSideImageEndAction = (modelSide: ModelSide) =>
    ({
        type: 'UPLOAD_SIDE_IMAGE_END',
        modelSide
    }) as const;

export const uploadSideImageProgressAction = (modelSide: ModelSide, progress: number) =>
    ({
        type: 'UPLOAD_SIDE_IMAGE_PROGRESS',
        modelSide,
        progress
    }) as const;

export const uploadProductImageProgressAction = (progress: number | null) =>
    ({
        type: 'UPLOAD_PRODUCT_IMAGE_PROGRESS',
        progress
    }) as const;

export const upsertModelOptionsAction = (modelOptions: ModelOptions) =>
    ({
        type: 'UPSERT_MODEL_OPTIONS',
        modelOptions
    }) as const;

export const removeModelOptionsAction = (modelColor: ModelColor) =>
    ({
        type: 'REMOVE_MODEL_OPTIONS',
        modelColor
    }) as const;

export const setDisabledSidesAction = (disabledSides: DisabledModelSide[]) =>
    ({
        type: 'SET_DISABLED_SIDES',
        disabledModelSides: disabledSides
    }) as const;

export const setProductResizable = (resizable: boolean) =>
    ({
        type: 'SET_PRODUCT_RESIZABLE',
        resizable
    }) as const;

export const toggleArForResizableAction = (arEnabled: boolean) =>
    ({
        type: 'TOGGLE_AR_FOR_RESIZABLE',
        arEnabled
    }) as const;

export const setAlreadyDoneRedirectToSceneEditor = (value: boolean) =>
    ({
        type: 'SET_ALREADY_DONE_REDIRECT_TO_SCENE_EDITOR',
        value
    }) as const;

export const setHeaderDescriptionCustomComponent = (component: React.ReactNode) =>
    ({
        type: 'SET_HEADER_DESCRIPTION_CUSTOM_COMPONENT',
        component
    }) as const;

export const setAreasWereDeletedAction = (value: boolean) =>
    ({
        type: 'SET_AREAS_WERE_DELETED',
        value
    }) as const;

export type ProductConfigurationActionType =
    | ReturnType<typeof newProductAction>
    | ReturnType<typeof setHeaderDescriptionCustomComponent>
    | ReturnType<typeof newProductNoIntegrationAction>
    | ReturnType<typeof editProductNameAction>
    | ReturnType<typeof setProductImageAction>
    | ReturnType<typeof setProductProviderCodeAction>
    | ReturnType<typeof modeAction>
    | ReturnType<typeof providerAction>
    | ReturnType<typeof setSalesChannelsAction>
    | ReturnType<typeof selectMerchantProductAction>
    | ReturnType<typeof selectProviderProductAction>
    | ReturnType<typeof addNewColorAction>
    | ReturnType<typeof removeColorAction>
    | ReturnType<typeof removeAllColorsAction>
    | ReturnType<typeof swapColorsAction>
    | ReturnType<typeof reorderColorsAction>
    | ReturnType<typeof editColorNameAction>
    | ReturnType<typeof addSideAction>
    | ReturnType<typeof mergeModelSideAction>
    | ReturnType<typeof editSideNameAction>
    | ReturnType<typeof editColorNameAndAttributesAction>
    | ReturnType<typeof editColorMappingAction>
    | ReturnType<typeof editColorJsonSettingsAction>
    | ReturnType<typeof editModelColorMappingsSalesChannelsAction>
    | ReturnType<typeof editSideMandatoryAllVariants>
    | ReturnType<typeof editSideMandatoryAllSides>
    | ReturnType<typeof editSideRotateAllVariants>
    | ReturnType<typeof editSideRotateAllSides>
    | ReturnType<typeof duplicateSideAction>
    | ReturnType<typeof swapSidesAction>
    | ReturnType<typeof reorderSidesAction>
    | ReturnType<typeof removeSideAction>
    | ReturnType<typeof applyAiSidesAction>
    | ReturnType<typeof addEmptyModelSideAction>
    | ReturnType<typeof removeModelSideAction>
    | ReturnType<typeof loadProductAction>
    | ReturnType<typeof changeStepAction>
    | ReturnType<typeof uploadSideImageStartAction>
    | ReturnType<typeof uploadSideImageEndAction>
    | ReturnType<typeof uploadSideImageProgressAction>
    | ReturnType<typeof uploadProductImageProgressAction>
    | ReturnType<typeof upsertModelOptionsAction>
    | ReturnType<typeof removeModelOptionsAction>
    | ReturnType<typeof setDisabledSidesAction>
    | ReturnType<typeof setProductResizable>
    | ReturnType<typeof toggleArForResizableAction>
    | ReturnType<typeof setAlreadyDoneRedirectToSceneEditor>
    | ReturnType<typeof setAreasWereDeletedAction>;
